/**
 * Monthly billing for service contracts.
 *
 * Pure functions only — no React, no Supabase. The Billing tab decides *when* to
 * generate; this file decides *what* a period is worth and *which* periods are
 * owed, so both are testable without a database.
 */
import type { ServiceContract, ServiceVisit, ContractInvoice } from '../types';

/** 'YYYY-MM' for a date, built from LOCAL parts.
 *  `toISOString()` would convert to UTC first and push the 1st of a month into
 *  the previous one for anyone west of Greenwich — the whole of the Americas. */
export function periodKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** Parse 'YYYY-MM' into a local Date on the 1st. Null when malformed. */
export function periodStart(period: string): Date | null {
    const m = /^(\d{4})-(\d{2})$/.exec(period || '');
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    if (month < 1 || month > 12) return null;
    return new Date(year, month - 1, 1);
}

/** Last calendar day of a period. Day 0 of the NEXT month is the last of this. */
export function periodEnd(period: string): Date | null {
    const start = periodStart(period);
    if (!start) return null;
    return new Date(start.getFullYear(), start.getMonth() + 1, 0);
}

/** Human label, e.g. "August 2026", in the caller's locale. */
export function periodLabel(period: string, locale = 'en-US'): string {
    const start = periodStart(period);
    if (!start) return period;
    return start.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
}

/** The period immediately before this one, rolling the year at January. */
export function previousPeriod(period: string): string | null {
    const start = periodStart(period);
    if (!start) return null;
    return periodKey(new Date(start.getFullYear(), start.getMonth() - 1, 1));
}

/**
 * What a contract is owed for one month.
 *
 *  - **Monthly** billing is the flat `monthlyRate` — the client pays for the
 *    coverage, not per visit, so a light month still bills in full.
 *  - **PerVisit** billing sums the visits actually COMPLETED in that month at
 *    `visitRate`. Scheduled and skipped visits are worth nothing: billing for a
 *    visit that never happened is the error that matters here.
 */
export function amountForPeriod(
    contract: ServiceContract,
    period: string,
    visits: ServiceVisit[],
): number {
    if (contract.billingType === 'Monthly') {
        return contract.monthlyRate || 0;
    }
    const completed = visits.filter(v =>
        v.contractId === contract.id
        && v.status === 'Completed'
        && typeof v.date === 'string'
        && v.date.slice(0, 7) === period
    );
    return completed.length * (contract.visitRate || 0);
}

/** Was this contract live at any point during the period? */
export function contractCoversPeriod(contract: ServiceContract, period: string): boolean {
    const start = periodStart(period);
    const end = periodEnd(period);
    if (!start || !end) return false;

    // A cancelled contract stops billing; a paused one is deliberately still
    // billable — pausing stops the VISITS, not a monthly retainer. The office
    // can delete the invoice if a pause was meant to stop billing too.
    if (contract.status === 'Cancelled') return false;

    const began = contract.startDate ? new Date(contract.startDate) : null;
    if (began && began > end) return false;

    const ended = contract.endDate ? new Date(contract.endDate) : null;
    if (ended && ended < start) return false;

    return true;
}

export interface DraftInvoice {
    contractId: string;
    period: string;
    issuedOn: string;
    dueOn: string;
    amount: number;
}

/** ISO date (YYYY-MM-DD) from local parts — same reason as periodKey. */
/** Local calendar day as YYYY-MM-DD. Local, not UTC: `toISOString()` after 5pm
 *  in a negative offset returns tomorrow, which is how an invoice ends up dated
 *  a day ahead of the evening it was actually sent. */
export function isoDay(d: Date = new Date()): string {
    return isoDate(d);
}

function isoDate(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Used when neither the contract nor the workspace says otherwise. */
export const DEFAULT_SERVICE_DUE_DAY = 10;

/**
 * The day of the month an invoice for `period` is due.
 *
 * Two shapes, decided by how the contract bills:
 *
 *  - **Monthly** — the client pays for coverage, so it is charged up front and
 *    falls due within the month being served. August service, due day 10 →
 *    2026-08-10.
 *  - **PerVisit** — the amount is not known until the visits have happened, so
 *    the invoice closes the month and falls due on the same day of the NEXT
 *    month. August service, due day 10 → 2026-09-10. Billing it "in advance"
 *    is not possible: on the 1st the total is still zero.
 *
 * Short months clamp rather than roll over. Due day 31 in February is the 28th
 * (29th in a leap year), never the 3rd of March — `setDate(31)` on a February
 * date silently lands in the next month, which is how a due date ends up in the
 * wrong month entirely.
 */
export function dueDateFor(period: string, billingType: 'Monthly' | 'PerVisit', dueDay: number): string | null {
    const start = periodStart(period);
    if (!start) return null;

    // PerVisit closes the month, so its due date belongs to the month after.
    const monthOffset = billingType === 'Monthly' ? 0 : 1;
    const year = start.getFullYear();
    const month = start.getMonth() + monthOffset;

    const lastDay = new Date(year, month + 1, 0).getDate();
    const day = Math.min(Math.max(Math.trunc(dueDay) || DEFAULT_SERVICE_DUE_DAY, 1), lastDay);
    return isoDate(new Date(year, month, day));
}

/** The contract's own terms, the workspace default, then the built-in. */
export function resolveDueDay(contract: ServiceContract, workspaceDefault?: number): number {
    const candidate = contract.paymentDueDay ?? workspaceDefault ?? DEFAULT_SERVICE_DUE_DAY;
    const n = Math.trunc(Number(candidate));
    return Number.isFinite(n) && n >= 1 && n <= 31 ? n : DEFAULT_SERVICE_DUE_DAY;
}

/**
 * Which invoices are missing for a period.
 *
 * Skips contracts that already have one — generating twice must never
 * double-bill. The database also enforces this with a UNIQUE constraint on
 * (contract_id, period); this check is what keeps the UI honest, the constraint
 * is what makes it true even with two people pressing the button at once.
 *
 * Zero-amount drafts are skipped too: a PerVisit contract with no completed
 * visits that month owes nothing, and issuing a $0 invoice is just noise the
 * office has to clear.
 *
 * @param workspaceDueDay default day-of-month terms; a contract's own
 *        `paymentDueDay` wins over it.
 */
export function pendingInvoicesFor(
    period: string,
    contracts: ServiceContract[],
    visits: ServiceVisit[],
    existing: ContractInvoice[],
    workspaceDueDay?: number,
    /** The day the invoice is actually being created. Injected rather than read
     *  from the clock so the dates are testable. */
    today: Date = new Date(),
): DraftInvoice[] {
    const start = periodStart(period);
    const end = periodEnd(period);
    if (!start || !end) return [];

    const already = new Set(
        existing.filter(i => i.period === period).map(i => i.contractId)
    );

    return contracts
        .filter(c => !already.has(c.id))
        .filter(c => contractCoversPeriod(c, period))
        .map(c => {
            const dueDay = resolveDueDay(c, workspaceDueDay);
            // "Issued" is a statement of fact about THIS document: the day it was
            // produced. It used to be derived from the period — the 1st for
            // Monthly, the last day for PerVisit — which put a date on the page
            // that never happened. Press generate on 10 August and the client
            // received a sheet claiming it was issued on 31 August, three weeks
            // in the future. The PERIOD line already says what is being billed;
            // the issue date does not need to repeat it.
            const issuedOn = isoDate(today);
            const scheduledDue = dueDateFor(period, c.billingType, dueDay) || isoDate(end);
            return {
                contractId: c.id,
                period,
                issuedOn,
                // A due date earlier than the issue date is nonsense on a
                // document a client reads. Billing a closed month late is
                // legitimate — it is simply payable on receipt, not retroactively
                // overdue before it existed.
                dueOn: scheduledDue < issuedOn ? issuedOn : scheduledDue,
                amount: amountForPeriod(c, period, visits),
            };
        })
        .filter(d => d.amount > 0);
}

export interface BillingTotals {
    invoiced: number;
    collected: number;
    outstanding: number;
    overdueCount: number;
}

/**
 * Totals for a set of invoices.
 *
 * `outstanding` is everything not yet Paid — including Drafts, because money
 * you have not billed for is still money you have not got.
 */
export function billingTotals(invoices: ContractInvoice[], today = new Date()): BillingTotals {
    let invoiced = 0;
    let collected = 0;
    let overdueCount = 0;

    for (const inv of invoices) {
        const amount = inv.amount || 0;
        invoiced += amount;
        if (inv.status === 'Paid') {
            collected += amount;
        } else if (inv.dueOn && new Date(`${inv.dueOn}T23:59:59`) < today) {
            // Derived, not stored: an invoice becomes overdue by the calendar
            // passing, and nothing runs nightly to flip a stored flag.
            overdueCount++;
        }
    }

    return { invoiced, collected, outstanding: invoiced - collected, overdueCount };
}
